import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, api } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { LanguagePicker } from '../i18n/LanguagePicker.js';
import { Alert, Button, formText, TextField } from '../ui/index.js';

/**
 * The screen a person sees when they have an account and no workspace.
 *
 * Registration deliberately does not create one. It always answers the same way whether or not the
 * address already has an account — that is what stops it being used to discover which addresses are
 * registered — and a route that cannot tell a new account from an existing one must not provision
 * anything.
 *
 * So the workspace is created here instead, and this handles every way somebody arrives without
 * one: a fresh sign-up, a revoked membership, a workspace that was closed. Before this existed, a
 * new account signed in successfully and landed in a shell where every screen answered "workspace
 * not found", which is the worst possible first minute.
 */

/**
 * A slug from a name.
 *
 * Suggested, never imposed: the field is editable and shows what will be used, because the slug
 * ends up in URLs and somebody should see it before it is theirs. Accents are folded rather than
 * dropped, so "Müller & Zoon" suggests `muller-zoon` rather than `mller-zoon`.
 */
export function suggestSlug(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/^-+|-+$/gu, '')
    .slice(0, 64);
}

export function FirstWorkspaceScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { email, refresh } = useAuth();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  /* Tracked so the suggestion stops overwriting a slug somebody typed themselves, while still
     following the name for everybody who never touches the field. */
  const [slugEdited, setSlugEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (!slugEdited) setSlug(suggestSlug(name));
  }, [name, slugEdited]);

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      const wanted = formText(data, 'name');
      const chosen = formText(data, 'slug') || suggestSlug(wanted);

      setBusy(true);
      setFailure(null);

      void api
        .post('/api/v1/tenants', { name: wanted, slug: chosen, defaultLocale: locale })
        .then(async () => {
          /* The shell reads the workspace list from the session, so it has to be re-read before
             this screen stands down — otherwise the person lands back here. */
          await refresh();
        })
        .catch((error: unknown) => {
          setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
        })
        .finally(() => setBusy(false));
    },
    [locale, refresh],
  );

  return (
    <div className="auth">
      {/* The language picker belongs here as much as on the sign-in screen: somebody who cannot
          read this screen cannot get past it, and it is the last one before the shell. */}
      <div className="auth__aside">
        <LanguagePicker />
      </div>

      <section className="auth__panel">
        <header className="auth__header">
          <p className="card__title">{t('app.name')}</p>
          <h1 className="auth__title">{t('firstWorkspace.title')}</h1>
          <p className="auth__subtitle">{t('firstWorkspace.body')}</p>
        </header>

        {/*
          A taken address is reported on the field that caused it, not as a banner.
          
          A generic message at the top of a form leaves somebody re-reading three fields to work out
          which one to change. Everything else is genuinely about the request rather than about a
          value they typed, so that does get a banner.
        */}
        {failure !== null && failure !== 'CONFLICT' && (
          <Alert tone="critical" title={t('firstWorkspace.error')}>
            {t('firstWorkspace.errorBody')}
          </Alert>
        )}

        <form className="form" onSubmit={onSubmit}>
          <TextField
            name="name"
            label={t('firstWorkspace.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
            required
          />
          <TextField
            name="slug"
            label={t('firstWorkspace.slug')}
            hint={t('firstWorkspace.slugHint')}
            {...(failure === 'CONFLICT' ? { error: t('firstWorkspace.taken') } : {})}
            value={slug}
            onChange={(event) => {
              setSlugEdited(true);
              setSlug(event.target.value);
            }}
            pattern="[a-z0-9][a-z0-9\-]*[a-z0-9]"
            minLength={3}
            maxLength={64}
            required
          />
          <Button type="submit" variant="primary" disabled={busy} loading={busy}>
            {t('firstWorkspace.create')}
          </Button>
        </form>

        {email !== null && (
          <p className="auth__footer">{t('firstWorkspace.signedInAs', { email })}</p>
        )}
      </section>
    </div>
  );
}
